/* eslint-disable max-lines */
import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, Stack, Tab, Tabs, useTheme } from '@mui/material';
import { SetStateAction, useCallback, useState } from 'react';
import { QueryObserverResult, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { CaseAuditLog } from 'src/components/atlas/organisms/CaseAuditLog';
import { CaseReportTab } from 'src/components/atlas/organisms/CaseReportTab/CaseReportTab';
import { CaseWorkspace } from 'src/components/atlas/organisms/CaseWorkspace/CaseWorkspace';
import { Button, CardBody, CardContainer, Divider } from 'src/components/atoms';
import { DefaultOption } from 'src/components/molecules';
import { CaseScreenTitle } from 'src/components/molecules/CaseScreenTitle/CaseScreenTitle';
import { CaseAuditLog as LegacyCaseAuditLog } from 'src/components/moleculesLegacy';
import { NewReportDetails } from 'src/components/organisms';
import { ReviewCaseTeamMemberModal } from 'src/components/organisms/Case/ReviewCaseTeamMemberModal';
import { ButtonContainer } from 'src/components/organisms/Case/ReviewCaseTeamMemberModal/ReviewCaseTeamMemberModal.styles';
import { CaseHeader } from 'src/components/organisms/CaseHeader/CaseHeader';
import {
  InvestigationPlanModal,
  WorkspaceInvestigation,
} from 'src/components/organisms/Investigation';
import { AddMitigationPlanDrawer } from 'src/components/organisms/MitigationPlan/AddMitigationPlanDrawer';
import { ReportMessagesPanel } from 'src/components/organisms/ReportMessagesDrawer/ReportMessagesPanel';
import {
  CaseDocuments,
  CaseScreenHeader,
} from 'src/components/organismsLegacy';
import { AddPerpetratorModal } from 'src/components/organismsLegacy/AddPerpetratorModal/AddPerpetratorModal';
import {
  CaseByIdQuery,
  CaseType,
  CategoryDisplayType,
  DisclosureCaseByIdQuery,
  MitigationPlanFragment,
  ReportStatus,
  useAddExternalNamedOffenderMutation,
  useAddInternalNamedOffenderMutation,
  useCreateAuditEntryMutation,
  useMarkCaseAsReadMutation,
  useMitigationPlansForDisclosureQuery,
  UserType,
} from 'src/graphql';
import { useIsFeatureEnabled, useReportMessages } from 'src/hooks';
import { UseCaseDocuments } from 'src/hooks/cases/useCaseDocuments';
import { useInvestigationPlan } from 'src/hooks/useInvestigationPlan';
import { AppDispatch } from 'src/store';
import theme from 'src/theme/theme';
import {
  AuditActions,
  CaseTypeEnum,
  FeatureFlag,
  TranscriptLanguages,
} from 'src/types';
import { isAssigneeInCase } from 'src/utils';
import {
  getUserCaseRole,
  trackReportTranslationEvent,
  TrackReportTranslationProps,
} from 'src/utils/analytics';
import { addTime, TimeUnit } from 'src/utils/time';
import * as Types from '../../../types';
import { markCaseAsRead } from './actions/markCaseAsRead';
import {
  addOrLinkNamedOffender,
  casePageVm,
  caseSelectedTranslationUpdated,
  toggleShowAddNamedOffenderModal,
  translationModalOpenStateUpdated,
} from './Case.slice';
import { CaseContainer, StyledTabsWithButtonGroup } from './Case.styles';
import { EventTranslationModal } from './EventTranslationModal';
import { legalDescription } from './helpers/legalDescription';
import { mapSubmissionModeToRoute } from './helpers/mapSubmissionModeToRoute';
import { getCaseNamedOffenders } from './helpers/namedOffender/getNamedOffenders';
import { getSubmissionRoute } from './helpers/permissionsConfig';

const availableCaseLeads = (c: CaseType) =>
  c.AllCaseLeads?.map((m) => ({
    label: m.name ?? 'Unnamed User',
    value: m.id,
  })) ?? [];

export interface CaseDetailsLayoutProps {
  caseType: CaseTypeEnum;
  caseId: string;
  caseById: CaseType;
  viewer: UserType;
  refetch: () => Promise<QueryObserverResult<CaseByIdQuery, unknown>>;
  addCaseCategory: (vars: { categoryId: number; caseId: string }) => void;
  removeCaseCategory: (vars: { categoryId: number; caseId: string }) => void;
  availableCategories: CategoryDisplayType[];
  categories: CategoryDisplayType[];
  caseDocuments: UseCaseDocuments;
  isSaving: boolean;
  setIsSaving: (saving: boolean) => void;
  showDynamicQuestions?: boolean;
  qaServiceData?: {
    qaResponseId?: string;
    qaData?: DisclosureCaseByIdQuery;
    isQaLoading?: boolean;
    isQaError?: boolean;
  };
}

export const CaseDetailsLayout = ({
  caseType,
  caseId,
  caseById,
  viewer,
  refetch,
  addCaseCategory,
  removeCaseCategory,
  availableCategories,
  categories,
  caseDocuments,
  isSaving,
  setIsSaving,
  showDynamicQuestions = false,
  qaServiceData,
}: CaseDetailsLayoutProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { tokens, presets } = useTheme<LensTheme>();
  const { TabsPresets } = presets;

  const queryClient = useQueryClient();
  const normalizeReportDataEnabled = useIsFeatureEnabled(
    FeatureFlag.USE_NORMALIZED_REPORT_DATA,
  );
  const segmentEnabled = useIsFeatureEnabled(
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );
  const aiInvestigatorEnabled = useIsFeatureEnabled(
    FeatureFlag.AI_INVESTIGATOR,
  );
  const isLegacyMode = useSelector(
    (state: Types.State) => state.organisation?.isLegacyMode,
  );

  const { mutateAsync: mutateMarkCaseAsRead } = useMarkCaseAsReadMutation();

  const { mutateAsync: mutationAddInternalNamedOffender } =
    useAddInternalNamedOffenderMutation();

  const { mutateAsync: mutationAddExternalNamedOffender } =
    useAddExternalNamedOffenderMutation();

  const reduxCase = useSelector(casePageVm);
  const [selectedTranslation, setSelectedTranslation] =
    useState<TranscriptLanguages>(
      reduxCase.caseSelectedTranslation ?? TranscriptLanguages.English,
    );
  const [trackCalled, setTrackCalled] = useState(false);

  const {
    plan: investigationPlan,
    isModalOpen: isInvestigationModalOpen,
    setIsModalOpen: setIsInvestigationModalOpen,
    generatePlan,
    acceptPlan,
    updateChecklist,
    updatePlanContent,
    isInvestigationActive,
  } = useInvestigationPlan(
    caseId,
    caseById.readableId ?? '',
    aiInvestigatorEnabled,
  );

  const {
    draftMessage,
    setDraftMessage,
    setTranslateMessages,
    messages,
    openMessages,
    closeMessages,
    sendMessage,
    isMessagesLoading,
  } = useReportMessages();

  const handleCloseMessages = useCallback(() => {
    closeMessages();
    void refetch();
  }, [closeMessages, refetch]);

  const { activeTab, selectActiveTab } = caseDocuments;
  const firstReport = caseById.Reports?.[0];
  const caseStatus = caseById.status ?? undefined;

  const isCoiType = caseType === CaseTypeEnum.COI;
  const isSpeakUpType = caseType === CaseTypeEnum.SPEAK_UP;

  const createAuditEntryMutation = useCreateAuditEntryMutation();

  const submissionRoute = firstReport
    ? getSubmissionRoute(
        mapSubmissionModeToRoute(firstReport),
        caseById.AssociatedPermissions?.canViewCaseDetails,
      )
    : undefined;

  const handleTrackReportTranslationEvent = () => {
    if (!segmentEnabled || trackCalled) return;
    trackReportTranslationEvent({
      organisation_id: reduxCase.organisationId,
      user_id: viewer.id,
      case_id: caseById.id,
      report_id: reduxCase.reportId,
      user_role: getUserCaseRole(
        viewer.id,
        caseById.CaseTeam,
        caseById.CaseLead?.id,
        caseById.Reports ?? [],
      ),
      source: firstReport!.source,
      permissions: Object.keys(reduxCase.permissions),
      new_translation: selectedTranslation,
      old_translation:
        reduxCase.caseSelectedTranslation ?? TranscriptLanguages.English,
      reported_category: firstReport!.ReportedCategory?.name ?? '',
      determined_category: firstReport!.DeterminedCategory?.name ?? '',
      intake_method: firstReport!.source,
    } as TrackReportTranslationProps);
    setTrackCalled(true);
    setTimeout(() => setTrackCalled(false), 5000);
  };

  const handleTranslationConfirm = () => {
    if (selectedTranslation !== reduxCase.caseSelectedTranslation) {
      createAuditEntryMutation.mutate({
        auditAction: AuditActions.TRANSLATE_REPORT,
        completed: true,
        auditData: { language: selectedTranslation, report: firstReport?.id },
      });
    }
    dispatch(caseSelectedTranslationUpdated(selectedTranslation));
    dispatch(translationModalOpenStateUpdated({ open: false }));
    handleTrackReportTranslationEvent();
  };

  const handleMarkCaseAsRead = () =>
    dispatch(
      markCaseAsRead({
        userId: viewer?.id,
        caseId,
        mutateMarkCaseAsRead,
        queryClient,
      }),
    );

  const setAddPerpetratorModalOpen = (open: SetStateAction<boolean>) =>
    dispatch(
      toggleShowAddNamedOffenderModal({ open: Boolean(open.valueOf()) }),
    );

  const closeAddPerpetratorModal = () =>
    dispatch(toggleShowAddNamedOffenderModal({ open: false }));

  const addPerpetrator = ({
    selectedUser,
    externalName,
    internal,
  }: {
    selectedUser: UserType | null;
    externalName: string;
    internal: boolean;
  }) => {
    if (!reduxCase.selectedEventId) return;
    dispatch(
      addOrLinkNamedOffender({
        reportId: reduxCase.reportId,
        eventId: reduxCase.selectedEventId,
        caseId,
        organisationId: reduxCase.organisationId,
        externalNamedOffenderToLink: reduxCase.externalNamedOffenderToLink,
        selectedUser,
        mutationAddInternalNamedOffender,
        mutationAddExternalNamedOffender,
        queryClient,
        externalName,
        internal,
      }),
    );
  };

  const employeeOptions = reduxCase.externalNamedOffenderToLink
    ? [
        {
          value: 'Employee directory',
          dataTestId: 'add-internal-perpetrator',
        },
      ]
    : undefined;

  const speakUpReportSection = () => {
    return (
      <NewReportDetails
        key={`report-details-${caseId}`}
        reportId={reduxCase.reportId}
        openMessages={openMessages}
        showDynamicQuestions={showDynamicQuestions}
        qaData={qaServiceData?.qaData}
        isQaLoading={qaServiceData?.isQaLoading}
        isQaError={qaServiceData?.isQaError}
      />
    );
  };

  const [value, setValue] = useState(0);
  const [mitigationDrawerOpen, setMitigationDrawerOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<MitigationPlanFragment | null>(
    null,
  );

  const { refetch: refetchMitigationPlans, data: mitigationPlansData } =
    useMitigationPlansForDisclosureQuery(
      { caseId },
      {
        enabled: !isLegacyMode && isCoiType,
      },
    );

  const userMap = Object.fromEntries(
    [
      ...(caseById.AllCaseLeads ?? []),
      ...(caseById.AllCaseTeamMembers ?? []),
    ].map((u) => [
      u.id,
      { name: u.name ?? `User ${u.id}`, role: u.role ?? undefined },
    ]),
  );

  const firstMitigationPlan =
    mitigationPlansData?.mitigationPlansForDisclosure[0] ?? null;

  if (!isLegacyMode && isCoiType) {
    return (
      <>
        <Box
          sx={{
            padding: `${tokens.core.spacing[3].value} ${tokens.core.spacing[6].value}`,
          }}
        >
          <CaseHeader
            caseType={caseType}
            caseStatus={caseStatus}
            openMessages={openMessages}
            reportId={firstReport?.id}
            caseId={caseId}
            onMitigate={
              isCoiType
                ? () => {
                    setEditingPlan(null);
                    setMitigationDrawerOpen(true);
                  }
                : undefined
            }
            refetch={() => {
              void refetch();
              refetchMitigationPlans();
            }}
            onInitiateInvestigation={
              aiInvestigatorEnabled ? generatePlan : undefined
            }
            isInvestigationActive={isInvestigationActive}
          />
          <Stack
            sx={{
              marginTop: tokens.core.spacing[1].value,
            }}
          >
            <Tabs
              aria-label="case tabs"
              onChange={(_, newVal) => setValue(newVal)}
              value={value}
              {...TabsPresets.Tabs.alignToPageHeader}
              sx={{
                alignSelf: 'stretch',
                borderBottom: `1px solid ${theme.atlas.semantic.color.ui.divider.default}`,
              }}
            >
              <Tab label="Report" id="tab-0" aria-controls="tabpanel-0" />
              <Tab label="Workspace" id="tab-1" aria-controls="tabpanel-1" />
              <Tab label="Audit log" id="tab-2" aria-controls="tabpanel-2" />
            </Tabs>
            <div
              role="tabpanel"
              hidden={value !== 0}
              id="tabpanel-0"
              aria-labelledby="tab-0"
            >
              {value === 0 && (
                <CaseReportTab
                  caseById={caseById}
                  caseType={caseType}
                  caseStatus={caseStatus ?? ''}
                  categories={categories}
                  firstReport={firstReport}
                  firstMitigationPlan={firstMitigationPlan}
                  formTitle={
                    qaServiceData?.qaData?.disclosureCaseById?.questionnaire
                      ?.publicTitle ?? ''
                  }
                  submissionRoute={submissionRoute}
                />
              )}
            </div>
            <div
              role="tabpanel"
              hidden={value !== 1}
              id="tabpanel-1"
              aria-labelledby="tab-1"
            >
              {value === 1 && (
                <CaseWorkspace
                  caseId={caseId}
                  userMap={userMap}
                  isSaving={isSaving}
                  setIsSaving={setIsSaving}
                  refetch={refetch}
                  documents={caseById.CaseDocuments ?? []}
                  caseDocuments={caseDocuments}
                  investigationPlan={
                    aiInvestigatorEnabled ? investigationPlan : undefined
                  }
                  onInvestigationChecklistUpdate={
                    aiInvestigatorEnabled ? updateChecklist : undefined
                  }
                  caseType={caseType}
                  onAddMitigationPlan={() => {
                    setEditingPlan(null);
                    setMitigationDrawerOpen(true);
                  }}
                  onEditMitigationPlan={(plan) => {
                    setEditingPlan(plan);
                    setMitigationDrawerOpen(true);
                  }}
                  caseStatus={caseStatus}
                />
              )}
            </div>
            <div
              role="tabpanel"
              hidden={value !== 2}
              id="tabpanel-2"
              aria-labelledby="tab-2"
            >
              {value === 2 && (
                <Box sx={{ padding: tokens.core.spacing[4].value }}>
                  <CaseAuditLog caseId={caseId} />
                </Box>
              )}
            </div>
          </Stack>
        </Box>
        {reduxCase.permissions.canEditCaseWorkspaceTab && isCoiType && (
          <AddMitigationPlanDrawer
            open={mitigationDrawerOpen}
            caseId={caseId}
            editPlan={editingPlan}
            onClose={() => {
              setMitigationDrawerOpen(false);
              setEditingPlan(null);
            }}
            onSuccess={() => {
              setMitigationDrawerOpen(false);
              setEditingPlan(null);
              refetchMitigationPlans();
              refetch();
            }}
          />
        )}
        <ReportMessagesPanel
          isLegacyMode={isLegacyMode}
          open={reduxCase.messagesVisible}
          setTranslateMessages={setTranslateMessages}
          messages={messages?.getMessagesByReport ?? []}
          closeMessages={handleCloseMessages}
          sendMessage={sendMessage}
          draftMessage={draftMessage}
          updateDraftMessage={(e) => setDraftMessage(e.target.value)}
          isAssignee={isAssigneeInCase(caseById, viewer.id)}
          caseId={caseId}
          channel={firstReport!.id}
          isLoading={isMessagesLoading}
          caseStatus={caseStatus}
          caseType={caseType}
        />
        {isSpeakUpType && (
          <AddPerpetratorModal
            open={reduxCase.showAddNamedOffenderModal}
            setOpen={setAddPerpetratorModalOpen}
            onClose={closeAddPerpetratorModal}
            usersToExclude={reduxCase.usersToExcludeInNamedOffenderModal}
            callback={addPerpetrator}
            employeeOptions={employeeOptions}
            isPostSubmission
          />
        )}
        {isSpeakUpType && aiInvestigatorEnabled && (
          <InvestigationPlanModal
            open={isInvestigationModalOpen}
            caseId={caseId}
            caseDisplayId={caseById.readableId ?? ''}
            plan={investigationPlan}
            onClose={() => setIsInvestigationModalOpen(false)}
            onAccept={() => {
              acceptPlan();
              selectActiveTab(1);
            }}
            onSaveEdit={updatePlanContent}
          />
        )}
      </>
    );
  }

  return (
    <CaseContainer
      className="routing-body-content"
      id="routing-body-content"
      data-testid="case-details-layout"
    >
      <EventTranslationModal
        onConfirm={handleTranslationConfirm}
        onCancel={() =>
          dispatch(translationModalOpenStateUpdated({ open: false }))
        }
        onChange={(selected) =>
          selected
            ? setSelectedTranslation(
                (selected as DefaultOption).value as TranscriptLanguages,
              )
            : undefined
        }
        currentLanguage={selectedTranslation}
        legalDescription={legalDescription}
        data-testid="event-translation-modal"
      />
      <CaseScreenTitle
        caseType={caseType}
        onInitiateInvestigation={
          aiInvestigatorEnabled ? generatePlan : undefined
        }
        isInvestigationActive={isInvestigationActive}
      />
      <CaseScreenHeader
        caseType={caseType}
        addCaseCategory={addCaseCategory}
        removeCaseCategory={removeCaseCategory}
        categories={categories}
        label={caseById.name ?? ''}
        status={caseStatus as ReportStatus}
        caseId={caseId}
        caseDisplayId={caseById.readableId ?? ''}
        expirationTime={addTime(
          90,
          TimeUnit.days,
          caseById?.createdAt ?? new Date(),
        )}
        caseLead={caseById.CaseLead ?? null}
        availableCaseLeads={availableCaseLeads(caseById)}
        caseTeamMembers={(caseById.Reports ?? [])
          .map((rep) => rep.Assignee)
          .filter((a): a is NonNullable<typeof a> => a != null)}
        viewer={viewer}
        availableCategoriesList={availableCategories}
        refetchCaseQuery={refetch}
        key={caseById.name ?? caseById.status}
        submittedAt={firstReport?.submittedAt ?? new Date()}
        caseAttributes={caseById.CaseAttributes}
        isMIReport={firstReport?.source === 'manual-case-creation'}
        namedOffenders={getCaseNamedOffenders(
          caseById,
          normalizeReportDataEnabled,
        )}
        isCaseSanitized={caseById.sanitizedAt != null}
        submissionRoute={submissionRoute}
      />
      <div>
        <StyledTabsWithButtonGroup
          headers={['Reports', 'Workspace', 'Audit Logs']}
          onClick={selectActiveTab}
          activeTab={activeTab}
          buttonWidth="120px"
          panels={[
            <CardContainer key="reports-tab">
              {isCoiType ? <></> : speakUpReportSection()}
            </CardContainer>,
            <CardContainer key={`case-documents-${caseId}`}>
              <CardBody>
                {aiInvestigatorEnabled && isInvestigationActive ? (
                  <WorkspaceInvestigation
                    caseId={caseId}
                    plan={investigationPlan}
                    onChecklistUpdate={updateChecklist}
                  />
                ) : (
                  <CaseDocuments
                    caseId={caseId}
                    documents={caseById.CaseDocuments ?? []}
                    caseDocuments={caseDocuments}
                    isSaving={isSaving}
                    setIsSaving={setIsSaving}
                    refetch={refetch}
                    disableLegacyStyles
                  />
                )}
              </CardBody>
            </CardContainer>,
            <CardContainer key={`audit-log-${caseId}`}>
              <CardBody>
                <LegacyCaseAuditLog caseId={caseId} disableLegacyStyles />
              </CardBody>
            </CardContainer>,
          ]}
        />
      </div>
      <ReportMessagesPanel
        isLegacyMode={isLegacyMode}
        open={reduxCase.messagesVisible}
        setTranslateMessages={setTranslateMessages}
        messages={messages?.getMessagesByReport ?? []}
        closeMessages={handleCloseMessages}
        sendMessage={sendMessage}
        draftMessage={draftMessage}
        updateDraftMessage={(e) => setDraftMessage(e.target.value)}
        isAssignee={isAssigneeInCase(caseById, viewer.id)}
        caseId={caseId}
        channel={firstReport!.id}
        isLoading={isMessagesLoading}
        caseStatus={caseStatus}
        caseType={caseType}
      />
      {isSpeakUpType && (
        <AddPerpetratorModal
          open={reduxCase.showAddNamedOffenderModal}
          setOpen={setAddPerpetratorModalOpen}
          onClose={closeAddPerpetratorModal}
          usersToExclude={reduxCase.usersToExcludeInNamedOffenderModal}
          callback={addPerpetrator}
          employeeOptions={employeeOptions}
          isPostSubmission
        />
      )}
      {isSpeakUpType && aiInvestigatorEnabled && (
        <InvestigationPlanModal
          open={isInvestigationModalOpen}
          caseId={caseId}
          caseDisplayId={caseById.readableId ?? ''}
          plan={investigationPlan}
          onClose={() => setIsInvestigationModalOpen(false)}
          onAccept={() => {
            acceptPlan();
            selectActiveTab(1);
          }}
          onSaveEdit={updatePlanContent}
        />
      )}
      {isSpeakUpType && reduxCase.showNewTeamMemberModal && (
        <ReviewCaseTeamMemberModal
          open={reduxCase.showNewTeamMemberModal}
          closeModal={handleMarkCaseAsRead}
          userId={viewer.id}
          caseId={caseId}
          footer={
            <>
              <Divider variant="horizontal" />
              <ButtonContainer>
                <Button
                  onClick={handleMarkCaseAsRead}
                  width="140px"
                  data-testid={'begin-case-button'}
                >
                  Begin Case
                </Button>
              </ButtonContainer>
            </>
          }
          showNoteTop={true}
        />
      )}
    </CaseContainer>
  );
};
